import { useEffect, useState, useMemo } from "react";
import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { Card, Container } from "@/shared/ui";
import { useAuthStore } from "@/shared/stores/authStore";
import { MaskToggleButton } from "./components/MaskToggleButton";
import { Profile } from "./assets/Profile";
import { Logo } from "./assets/Logo";
import styles from "./Header.module.scss";
import { Telegram } from "./assets/Telegram";
import { TelegramPopup } from "../TelegramPopup";
import { BalanceWidget } from "../BalanceWidget/BalanceWidget";
import { Tabs } from "./components/Tabs";
import { useSuppliersStore } from "@/shared/stores/suppliersStore";
import { ArrowLeftIcon } from "@/modules/ServiceCore/assets/icons";
import { serviceConfig } from "@/modules/ServiceCore/config/serviceConfig";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { TipIcon } from "@/modules/ServiceCore/assets/icons";
import { useUiStore } from "@/shared/stores/uiStore";
import { SubscriptionsIcon } from "./assets/SubscriptionsIcon";
import { DocumentsIcon } from "./assets/DocumentsIcon";

const TELEGRAM_URL = "https://t.me/selleralert";
const POPUP_SHOWN_KEY = "telegramPopupShown";
const IS_SHOW_BALANCE = import.meta.env.VITE_IS_SHOW_BALANCE
  ? import.meta.env.VITE_IS_SHOW_BALANCE === "true"
  : true;

function HomeHeaderContent() {
  const {
    suppliers,
    loading: suppliersLoading,
    selectedSupplierId,
    selectSupplier,
    fetchSuppliers,
  } = useSuppliersStore();

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  if (!suppliers && !suppliersLoading) {
    return null;
  }

  return (
    <Tabs
      selectedId={selectedSupplierId || ""}
      onSelect={selectSupplier}
      people={suppliers}
      loading={suppliersLoading}
      refetchSuppliers={fetchSuppliers}
    />
  );
}

function ServiceHeaderContent() {
  const navigate = useNavigate();
  const { serviceType, stage, navigateBack } = useServiceWorkflowStore();
  const config = serviceType ? serviceConfig[serviceType] : null;

  const handleBack = () => {
    if (!navigateBack()) {
      navigate({ to: "/" });
    }
  };

  return (
    <div className={styles.headerSection}>
      <div className={styles.headerGroup}>
        <button
          className={styles.backButton}
          onClick={handleBack}
          aria-label="Назад"
        >
          <ArrowLeftIcon className={styles.icon} />
        </button>
        <h1 className={styles.title}>{config?.title}</h1>
      </div>
      {stage === "status" && (
        <div className={styles.tablesStatus}>
          <TipIcon color="#9FD356" />
          <span>Подключено</span>
        </div>
      )}
    </div>
  );
}

function SubscriptionsHeaderContent() {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate({ to: "/" });
  };

  return (
    <div className={styles.headerSection}>
      <div className={styles.headerGroup}>
        <button
          className={styles.backButton}
          onClick={handleBack}
          aria-label="Назад"
        >
          <ArrowLeftIcon className={styles.icon} />
        </button>
        <h1 className={styles.title}>Мои подписки</h1>
      </div>
    </div>
  );
}

function LegalHeaderContent() {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate({ to: "/" });
  };

  return (
    <div className={styles.headerSection}>
      <div className={styles.headerGroup}>
        <button
          className={styles.backButton}
          onClick={handleBack}
          aria-label="Назад"
        >
          <ArrowLeftIcon className={styles.icon} />
        </button>
        <h1 className={styles.title}>Правовые документы</h1>
      </div>
    </div>
  );
}

export function Header() {
  const { openModal } = useUiStore();
  const [showPopup, setShowPopup] = useState(false);
  const username = useAuthStore((state) => state.profile?.first_name);
  const isAdmin = useAuthStore((state) => state.isAdmin());

  const location = useRouterState({ select: (s) => s.location });
  const isHomePage = location.pathname === "/";
  const isSubscriptionsPage = location.pathname === "/subscriptions";
  const isLegalPage = location.pathname === "/legal";

  const handleClosePopup = () => {
    setShowPopup(false);
    localStorage.setItem(POPUP_SHOWN_KEY, "true");
  };

  useEffect(() => {
    const hasBeenShown = localStorage.getItem(POPUP_SHOWN_KEY);
    if (!hasBeenShown) {
      const timer = setTimeout(() => {
        setShowPopup(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const childrenContent = useMemo(() => {
    if (location.pathname.startsWith("/service")) {
      return <ServiceHeaderContent />;
    }
    if (isSubscriptionsPage) {
      return <SubscriptionsHeaderContent />;
    }
    if (isLegalPage) {
      return <LegalHeaderContent />;
    }
    if (isHomePage) {
      return <HomeHeaderContent />;
    }
    return null;
  }, [isHomePage, isSubscriptionsPage, isLegalPage, location.pathname]);

  return (
    <>
      <Card className={styles.header}>
        <Container>
          <div className={styles.content}>
            <div className={styles.leftItems}>
              <Link to="/">
                <Logo />
                <div className={styles.greeting}>Привет, {username}!</div>
              </Link>
              <button
                onClick={() => window.open(TELEGRAM_URL, "_blank")}
                className={styles.iconButton}
                title="Telegram"
              >
                <Telegram />
              </button>
              <Link
                to="/subscriptions"
                className={styles.iconButton}
                title="Мои подписки"
              >
                <SubscriptionsIcon />
              </Link>
              <Link
                to="/legal"
                className={styles.iconButton}
                title="Правовые документы"
              >
                <DocumentsIcon />
              </Link>
              {showPopup && <TelegramPopup onClose={handleClosePopup} />}
            </div>
            <div className={styles.userSection}>
              <div className={styles.userButtons}>
                {isAdmin && (
                  <button
                    onClick={() => openModal("ADMIN_SWITCH_USER", {})}
                    className={styles.iconButton}
                    title="Сменить пользователя"
                  >
                    <Profile />
                  </button>
                )}
                <MaskToggleButton />
              </div>
              {IS_SHOW_BALANCE && <BalanceWidget />}
            </div>
          </div>
          {childrenContent}
        </Container>
      </Card>
    </>
  );
}
